/**
 * 印刷イベント通信サービス
 * 自動ラベル印刷のためのSSE接続と印刷ジョブ管理を提供
*/
import { EventSourcePolyfill } from 'event-source-polyfill';
import printAgentService from './printAgentService';
import service from './service';
import constants from '../constants/constants.json';

const printEventService = {
  eventSource: null,
  connected: false,
  retryCount: 0,
  maxRetries: 3,
  retryInterval: 3000,
  printJobQueue: [],
  isProcessingQueue: false,
  lastStoreId: null,
  
  /**
   * SSE接続を開始
   * @param {number} storeId 店舗ID
   * @returns {Promise<boolean>} 接続成功したかどうか
   */
  async connect(storeId) {
    if (this.connected && this.eventSource) {
      return true;
    }
    
    try {
      this.disconnect();
      
      const browserId = this.getBrowserId();
      
      const token = localStorage.getItem('token');
      const url = `api/print-events/connect?storeId=${storeId}&browserId=${browserId}`;

      // 認証キーを生成
      const authPrintEventkey = await this.generateHmacSignature(token, url);

      // JWT対応のためWrapperモジュールを使用
      this.eventSource = new EventSourcePolyfill(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'AuthPrintEventkey': authPrintEventkey // HMAC署名をヘッダーに追加
        },
        heartbeatTimeout: 60000 // 1分
      });
      
      this.eventSource.onopen = () => {
        this.connected = true;
        this.lastStoreId = storeId;
        this.retryCount = 0;
      };
      
      this.eventSource.addEventListener(constants.PRINT_JOB_EVENT_NAME, async (event) => {
        try {
          const job = JSON.parse(event.data);
          
          this.addJobToQueue(job);
        } catch (error) {
          console.error('印刷ジョブの処理中にエラーが発生しました:', error);
        }
      });
      
      this.eventSource.onerror = (error) => {
        console.error('印刷イベントSSE接続でエラーが発生しました:', error);
        this.connected = false;
        
        this.eventSource.close();
        this.eventSource = null;
        //  再接続の試行（3秒ごとに最大5回まで）
        if (this.retryCount < this.maxRetries) {
          this.retryCount++;
          
          setTimeout(() => {
            this.connect(storeId);
          }, this.retryInterval);
        }
      };
      // 疎通確認用に空のメッセージハンドラを設定
      this.eventSource.onmessage = () => {};
      
      return true;
    } catch (error) {
      console.error('印刷イベントSSE接続の確立に失敗しました:', error);
      return false;
    }
  },
  
  /**
   * SSE接続を閉じる
   */
  disconnect() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
      this.connected = false;
    }
    // サーバー側のSSE接続も明示的に切断
    const token = localStorage.getItem('token');
    const storeId = this.lastStoreId;
    const browserId = this.getBrowserId();
    if (token && storeId && browserId) {
      const url = `api/print-events/disable-connection?storeId=${storeId}&browserId=${browserId}`;
      fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
      });
    }
    // ブラウザIDを削除
    this.remobeBrowserId();
    this.lastStoreId = null;
    this.printJobQueue = [];
    this.isProcessingQueue = false;
  },

  /**
   * 印刷ジョブをキューに追加
   * @param {Object} job 印刷ジョブ
   */
  addJobToQueue(job) {
    this.printJobQueue.push(job);
    console.log(`印刷ジョブをキューに追加: ${job.productId} (キュー長: ${this.printJobQueue.length})`);
    
    this.processQueue();
  },

  /**
   * キューを順次処理
   */
  async processQueue() {
    if (this.isProcessingQueue || this.printJobQueue.length === 0) {
      return;
    }

    this.isProcessingQueue = true;
    console.log(`印刷ジョブキューの処理を開始 (${this.printJobQueue.length}件)`);

    while (0 < this.printJobQueue.length) {
      const job = this.printJobQueue.shift();
      
      try {
        console.log(`印刷ジョブを処理中: ${job.productId}`);
        await this.processPrintJob(job);
        console.log(`印刷ジョブ処理完了: ${job.productId}`);
      } catch (error) {
        console.error(`印刷ジョブ処理エラー: ${job.productId}`, error);
      }
    }

    this.isProcessingQueue = false;
    console.log('印刷ジョブキューの処理が完了しました');
  },
  
  /**
   * 印刷ジョブを処理
   * @param {Object} job 印刷ジョブ
   */
  async processPrintJob(job) {
    try {
      const labelResponse = await service.generateBulkProductLabels([job.productId]);
      
      if (!labelResponse.data.success) {
        throw new Error(`ラベル生成に失敗しました: ${labelResponse.data.message}`);
      }
      
      const labelSizeToPdfMap = labelResponse.data.labelSizeToPdfMap;
      if (!labelSizeToPdfMap) {
        //  PDFが作成されていない場合は、商品削除済みと見なし、ジョブを完了としてマーク
        return await this.updateJobStatus(job.id, constants.JobStatus.COMPLETED.id);
      }
      const labelSize = Object.keys(labelSizeToPdfMap)[0];
      const pdfBase64 = labelSizeToPdfMap[labelSize];

      //  印刷エージェントに印刷をリクエスト
      const printResult = await printAgentService.printLabel(
        pdfBase64,
        labelSize,
        `AutoPrint-${job.productId}`,
        true
      );
      //  ジョブステータス
      const printJobStatus = printResult ? constants.JobStatus.COMPLETED.id : constants.JobStatus.FAILED.id;
      //  印刷ジョブのステータスを更新
      await this.updateJobStatus(job.id, printJobStatus);
    } catch (error) {
      console.error('印刷ジョブの処理中にエラーが発生しました:', error);
      await this.updateJobStatus(job.id, constants.JobStatus.FAILED.id);
    }
  },
  
  /**
   * ジョブステータスを更新
   * @param {number} jobId ジョブID
   * @param {string} status 新しいステータス
   * @returns {Promise<Object>} 更新結果
   */
  async updateJobStatus(jobId, status) {
    try {
      const response = await service.updatePrintJobStatus(jobId, status);
      
      return await response.data;
    } catch (error) {
      console.error('ジョブステータスの更新中にエラーが発生しました:', error);
      throw error;
    }
  },

  /**
   * HMAC署名を生成
   * @param {string} token トークン
   * @param {string} url URL
   * @returns {Promise<void>}
   */
  async generateHmacSignature(token, url) {
    const encoder = new TextEncoder();
    const keyData = encoder.encode(token);
    const data = encoder.encode(url);

    // HMACキーをインポート
    const cryptoKey = await window.crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );
    // HMAC署名を生成
    const signature = await window.crypto.subtle.sign('HMAC', cryptoKey, data);
    // base64エンコード
    const hashArray = Array.from(new Uint8Array(signature));
    return btoa(String.fromCharCode.apply(null, hashArray));
  },

  getBrowserId() {
    const storageKey = 'enuzu_browser_id';
    let browserId = sessionStorage.getItem(storageKey);
    
    if (!browserId) {
      browserId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
      sessionStorage.setItem(storageKey, browserId);
    }
    
    return browserId;
  },

  remobeBrowserId() {
    const storageKey = 'enuzu_browser_id';
    sessionStorage.removeItem(storageKey);
  }
};

export default printEventService;

